import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Text that clips with an ellipsis and reveals itself in full on hover.
 *
 * NOTE: extracted from the `ProjectName` component that lived inside
 * pages/employee/EmployeeList.tsx, which already solved this exact problem and
 * is now a caller. Nothing here is new behaviour; it is the same component made
 * reusable so the notification panel did not become a second copy.
 *
 * WHY A PORTAL AND NOT `title`: the app's table cells use `TruncatedText`,
 * whose native `title` attribute is right for a cell in a wide, flat table. It
 * is the wrong tool inside a scroll container — the notification panel is
 * `max-h-[70vh] overflow-y-auto` and the project list sits in a modal, and an
 * absolutely-positioned tooltip is clipped at those overflow boundaries.
 * Rendering to <body> escapes them. It also lets the tooltip carry the app's
 * own styling and dark mode, which a native tooltip cannot.
 *
 * WHY ONLY WHEN CLIPPED: the tooltip appears only if `scrollWidth` actually
 * exceeds `clientWidth`. A tooltip that repeats text already fully visible is
 * noise, and on a dense list it fires constantly.
 */
export interface TruncatedWithTooltipProps {
  /** The full text. Clipped for display; shown whole in the tooltip. */
  text: string;
  /** Classes for the clipped text element. `truncate` is applied already. */
  className?: string;
  /** Classes for the wrapper that owns the hover area and the layout. */
  wrapperClassName?: string;
}

/** Gap in px between the text and the tooltip. */
const TOOLTIP_OFFSET = 8;
/** Below this much room above the text, the tooltip flips underneath. */
const FLIP_THRESHOLD = 72;

const TruncatedWithTooltip: React.FC<TruncatedWithTooltipProps> = ({
  text,
  className = "",
  wrapperClassName = "",
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean } | null>(null);

  const hide = useCallback(() => setTip(null), []);

  const show = () => {
    const element = textRef.current;
    // Not clipped → nothing the tooltip could add.
    if (!element || element.scrollWidth <= element.clientWidth) return;

    const rect = element.getBoundingClientRect();
    // NOTE: flips below when the text sits near the top of the window. The
    // notification panel hangs from a bell in the header, so its first rows
    // have almost no room above them and a fixed "always above" tooltip would
    // be cut off by the viewport.
    const below = rect.top < FLIP_THRESHOLD;

    setTip({
      x: rect.left + rect.width / 2,
      y: below ? rect.bottom + TOOLTIP_OFFSET : rect.top - TOOLTIP_OFFSET,
      below,
    });
  };

  /**
   * NOTE: the tooltip is positioned once, in viewport coordinates, so it would
   * be left stranded if the page or the panel scrolled under it. Scrolling and
   * resizing simply dismiss it — capture:true so a scroll on ANY ancestor is
   * caught, not just the window.
   */
  useEffect(() => {
    if (!tip) return;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [tip, hide]);

  return (
    <span
      className={`min-w-0 ${wrapperClassName}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      // Keyboard users get it too: the row is focusable, so focus reveals the
      // same text that hover does.
      onFocus={show}
      onBlur={hide}
    >
      <span ref={textRef} className={`block truncate ${className}`}>
        {text}
      </span>

      {tip &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              left: tip.x,
              top: tip.y,
              transform: `translate(-50%, ${tip.below ? "0" : "-100%"})`,
            }}
            className="pointer-events-none z-[9999] max-w-xs whitespace-normal break-words rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg dark:bg-slate-700"
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default TruncatedWithTooltip;
